import { Flex, Spin } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { ROLE_LABEL_STATUSES } from "../../../constants/role-labels.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { type RootState } from "../../../store.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode
} from "../../Branches/redux/branches.slice.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import { serializeData } from "../../../utils/utils"
import { fetchPendingDeletionCount } from "../../AccountDeletionRequestsPage/redux/account-deletion.thunks.ts"
import {
  isBlockingError,
  useProcessPermissions
} from "../../SettingsPermissions/use-process-permissions.tsx"
import { useLabelsUrlFilter } from "../hooks/use-labels-url-filter.ts"
import { PerformanceDashboard } from "../performance/PerformanceDashboard.tsx"
import { ComparisonTab } from "../performance/comparison/ComparisonTab.tsx"
import {
  changeUsersFilters,
  usersSetProp
} from "../redux/team-members.slice.ts"
import {
  createTeamMember,
  deleteTeamMember,
  fetchTeamMember,
  fetchTeamMembers,
  fetchTeamMembersFilterOptions,
  updateTeamMember
} from "../redux/team-members.thunks.ts"
import type { TeamMembersFilters } from "../redux/team-members.types.ts"
import {
  type TeamMember,
  type TeamMemberFormIntent,
  type TeamMemberMutationData
} from "../types/types.ts"
import { TeamManagementHeader } from "./TeamManagementHeader.tsx"
import { TeamManagementTabs } from "./TeamManagementTabs.tsx"
import { TeamMemberForm } from "./TeamMemberForm/TeamMemberForm.tsx"
import { TeamMemberPermissionsDrawer } from "./TeamMemberPermissionsDrawer.tsx"
import UsersList from "./TeamMembers/TeamMembers.tsx"

const TeamManagementPage = ({
  userDetailsFetching,
  userDetails,
  saveFetching,
  getUsers,
  filters,
  currentUserId,
  teamManagementPermissions,
  changeFilters,
  getUsersFilterOptions,
  createUser,
  getUserDetails,
  updateUser,
  deleteUser,
  setProp,
  pendingDeletionCount,
  getPendingDeletionCount,
  isCompanyMode,
  activeBranchId
}: ConnectedProps<typeof connector>) => {
  const { processPermissions, modalNode: blockingModalNode } =
    useProcessPermissions()
  const { ready } = useLabelsUrlFilter()
  const navigate = useNavigate()
  const canSeePerformance = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_team_performance
  )
  const [searchParams, setSearchParams] = useSearchParams()

  const [ordering, setOrdering] = useState<string | null>(null)
  const [showUserForm, setShowUserForm] = useState(false)
  const [formIntent, setFormIntent] = useState<TeamMemberFormIntent>("edit")

  const inviteAllowed = teamManagementPermissions?.features?.team_invite_user
  const createAllowed = teamManagementPermissions?.team?.team_create
  const deletionRequestsAllowed =
    teamManagementPermissions?.features?.team_manage_account_deletion

  // Scoped to an individual branch (not Company HQ): the active
  // workspace branch is the WHOLE scope, overriding whatever the page's
  // own (HQ-only, currently hidden) Branches filter last held — every
  // consumer of `filters` (the main table's own fetch AND
  // `TeamOrganizationView`'s independent one) reads through this, not
  // the raw persisted value, so neither is left showing the whole
  // company while "in" one branch's workspace.
  const effectiveFilters: TeamMembersFilters =
    !isCompanyMode && activeBranchId
      ? { ...filters, team_ids: activeBranchId }
      : filters

  const clearUserDetails = () => setProp({ key: "userDetails", value: null })

  const handleCreateTeamMember = () => {
    clearUserDetails()
    setFormIntent("edit")
    setShowUserForm(true)
  }

  const handleChangePage = useCallback(
    (page: number) => {
      changeFilters({ page })
    },
    [changeFilters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<TeamMembersFilters>) => {
      changeFilters({ ...newData, page: 1 })
    },
    [changeFilters]
  )

  const handleGetTeamMembers = useCallback(() => {
    const serializeFilters = serializeData(
      effectiveFilters || {}
    ) as TeamMembersFilters
    const params: TeamMembersFilters = { ...(serializeFilters || {}), ordering }
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    getUsers({ ...params, page: 1 })
  }, [effectiveFilters, ordering, getUsers])

  const handleSaveUser = useCallback(
    async (userData: TeamMemberMutationData) => {
      const id = userData?.id || userDetails?.id

      try {
        if (id) {
          try {
            await updateUser({ ...userData, id }).unwrap()
          } catch (e) {
            if (!isBlockingError(e)) throw e
            const resolved = await processPermissions(e.blocking)
            if (!resolved) throw e
            await updateUser({ ...userData, id }).unwrap()
          }
          clearUserDetails()
        } else {
          await createUser(userData).unwrap()
        }

        setShowUserForm(false)
        handleGetTeamMembers()
      } catch {
        // so that the edit row doesn't close
        if (id) {
          throw new Error("User action error")
        }
      }
    },
    [
      updateUser,
      createUser,
      handleGetTeamMembers,
      processPermissions,
      userDetails
    ]
  )

  const handleDeleteUser = useCallback(
    async (id: string) => {
      for (;;) {
        try {
          await deleteUser(id).unwrap()
          return
        } catch (e) {
          if (!isBlockingError(e)) return
          const resolved = await processPermissions(e.blocking)
          if (!resolved) return
        }
      }
    },
    [deleteUser, processPermissions]
  )

  const handleUserFormCancel = () => {
    if (userDetails) clearUserDetails()
    setShowUserForm(false)
  }

  const handleSelectUser = useCallback(
    (user: TeamMember, intent: TeamMemberFormIntent = "edit") => {
      getUserDetails(user.id)
      setFormIntent(intent)
      setShowUserForm(true)
    },
    [getUserDetails]
  )

  useEffect(() => {
    getUsersFilterOptions()
    generateDocumentTitle("Team Management")
  }, [])

  useEffect(() => {
    if (deletionRequestsAllowed) {
      getPendingDeletionCount()
    }
  }, [deletionRequestsAllowed, getPendingDeletionCount])

  useEffect(() => {
    if (!ready) return
    handleGetTeamMembers()
  }, [ready, handleGetTeamMembers])

  // The Setup Assistant's "Add team member" action deep-links here rather
  // than reaching into this page's own state — same pattern as the
  // calendar's `?event=<id>&edit=1`. Cleared immediately so navigating back
  // (or a plain refresh) doesn't reopen the form.
  useEffect(() => {
    if (searchParams.get("addMember") !== "1") return
    handleCreateTeamMember()
    setSearchParams(
      (prev) => {
        prev.delete("addMember")
        return prev
      },
      { replace: true }
    )
  }, [searchParams])

  return (
    <div className={"grid size-full"}>
      <Flex vertical gap={"middle"} className={"size-full overflow-auto"}>
        {blockingModalNode}
        <TeamManagementHeader
          inviteAllowed={inviteAllowed}
          createAllowed={createAllowed}
          pendingDeletionCount={pendingDeletionCount}
          deletionRequestsAllowed={deletionRequestsAllowed}
          showsPerformance={canSeePerformance}
          onInviteClick={handleCreateTeamMember}
          onDeletionRequestsClick={() =>
            navigate(RoutesPaths.account_deletion_requests)
          }
        />

        {showUserForm && userDetailsFetching && <Spin spinning fullscreen />}

        {formIntent === "permissions" && (
          <TeamMemberPermissionsDrawer
            open={showUserForm && !userDetailsFetching}
            memberName={userDetails ? getEntityName(userDetails) : undefined}
            initialPermissions={userDetails?.permissions?.codes}
            saving={saveFetching}
            onSave={handleSaveUser}
            onClose={handleUserFormCancel}
          />
        )}

        {formIntent === "edit" && showUserForm && !userDetailsFetching && (
          <TeamMemberForm
            isEdit={!!userDetails}
            isSelfEdit={userDetails?.id === currentUserId}
            isGccOwner={
              userDetails?.role?.label_code === ROLE_LABEL_STATUSES.OWNER
            }
            modifyPermissionsAllowed={
              teamManagementPermissions?.features?.team_modify_permissions
            }
            inviteAllowed={inviteAllowed}
            initialValues={
              userDetails
                ? {
                    ...userDetails,
                    permissions: userDetails?.permissions?.codes
                  }
                : undefined
            }
            onSave={handleSaveUser}
            onCancel={handleUserFormCancel}
          />
        )}

        <TeamManagementTabs
          canSeePerformance={canSeePerformance}
          ourTeam={
            <UsersList
              filters={effectiveFilters}
              ordering={ordering}
              currentUserId={currentUserId}
              onSelectUser={handleSelectUser}
              onPageChange={handleChangePage}
              onFilterChange={handleChangeFilter}
              onRetry={handleGetTeamMembers}
              onOrderingChange={setOrdering}
              onSaveUser={handleSaveUser}
              onDeleteUser={handleDeleteUser}
            />
          }
          performance={<PerformanceDashboard />}
          comparison={<ComparisonTab />}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  userDetailsFetching: state.users.userDetailsFetching,
  userDetails: state.users.userDetails,
  saveFetching: state.users.saveFetching,
  currentUserId: state.auth.userInfo?.id,
  teamManagementPermissions: state.auth.permissions?.codes?.team_management,
  filters: state.users.filters,
  pendingDeletionCount: state.accountDeletion?.pendingCount ?? 0,
  isCompanyMode: selectIsCompanyMode(state),
  activeBranchId: selectActiveBranchId(state)
})

const mapDispatchToProps = {
  createUser: createTeamMember,
  updateUser: updateTeamMember,
  deleteUser: deleteTeamMember,
  getUsers: fetchTeamMembers,
  getUserDetails: fetchTeamMember,
  setProp: usersSetProp,
  changeFilters: changeUsersFilters,
  getUsersFilterOptions: fetchTeamMembersFilterOptions,
  getPendingDeletionCount: fetchPendingDeletionCount
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(TeamManagementPage)
